import { FC, useState } from "react"
import BBSwitch from "../../../../../../form/Switch"
import { LeadworkflowDetails } from "../../../../../../../../types"

interface Props {
  values: LeadworkflowDetails
  setConfigField: (field: string, value: any) => void
  removeConfigField: (field: string) => void
}

interface BehaviourRowProps {
  label: string
  description?: string
  enabled: boolean
  onChange: (val: boolean) => void
  onDelete?: () => void
  last?: boolean
}

const BehaviourRow: FC<BehaviourRowProps> = ({
  label,
  description,
  enabled,
  onChange,
  onDelete,
  last
}) => (
  <div
    className={`flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors ${
      last ? "" : "border-b border-gray-100"
    }`}
  >
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-gray-700">{label}</p>
      {description && <p className="text-xs text-gray-400">{description}</p>}
    </div>
    <div className="flex items-center gap-3 ml-4 shrink-0">
      <BBSwitch enabled={enabled} setEnabled={onChange} />
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="text-gray-300 hover:text-red-400 transition-colors text-lg leading-none"
          aria-label={`Remove ${label}`}
        >
          ×
        </button>
      )}
    </div>
  </div>
)

const BEHAVIOUR_FLAGS = [
  {
    key: "branded",
    label: "Branded",
    description: "Show brand logo and colours inside the embed"
  },
  {
    key: "validation_required",
    label: "Validation required",
    description: "Require email/phone verification before showing results"
  },
  {
    key: "phone_required",
    label: "Phone required",
    description: "Make phone number a required field"
  },
  {
    key: "subscribe_enabled",
    label: "Subscribe CTA",
    description: "Show rate alert subscription option"
  },
  {
    key: "disable_pricing",
    label: "Disable pricing",
    description: "Hide pricing results from the embed"
  },
  {
    key: "apply_now_params_enabled",
    label: "Apply now params",
    description: "Pass query params to the apply-now URL"
  },
  {
    key: "reviews_enabled",
    label: "Reviews enabled",
    description: "Display lender reviews inside the embed"
  },
  {
    key: "show_txt_msgs_disclaimer",
    label: "SMS disclaimer",
    description: "Show text message opt-in consent"
  }
] as const

const DEFAULT_KEYS = new Set(BEHAVIOUR_FLAGS.map(f => f.key))

const KNOWN_NON_BOOLEAN_KEYS = new Set([
  "steps",
  "disclaimers",
  "primary_color",
  "exit_cta_step",
  "background_img"
])

const LeadworkflowBehaviour: FC<Props> = ({
  values,
  setConfigField,
  removeConfigField
}) => {
  const cfg = values.config_json
  const [adding, setAdding] = useState(false)
  const [newKey, setNewKey] = useState("")

  const extraFields = Object.entries(cfg).filter(
    ([k, v]) =>
      typeof v === "boolean" &&
      !DEFAULT_KEYS.has(k as any) &&
      !KNOWN_NON_BOOLEAN_KEYS.has(k)
  ) as [string, boolean][]

  const handleAdd = () => {
    const key = newKey.trim()
    if (!key) return
    setConfigField(key, false)
    setNewKey("")
    setAdding(false)
  }

  return (
    <div className="space-y-5 pt-2">
      <div className="rounded-xl border border-gray-200 overflow-hidden">
        {BEHAVIOUR_FLAGS.map(({ key, label, description }, i) => (
          <BehaviourRow
            key={key}
            label={label}
            description={description}
            enabled={cfg[key] === true}
            onChange={val => setConfigField(key, val)}
            last={i === BEHAVIOUR_FLAGS.length - 1}
          />
        ))}
      </div>

      {(extraFields.length > 0 || adding) && (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-500">Custom fields</p>
          </div>
          {extraFields.map(([key, value], i) => (
            <BehaviourRow
              key={key}
              label={key}
              enabled={value}
              onChange={val => setConfigField(key, val)}
              onDelete={() => removeConfigField(key)}
              last={i === extraFields.length - 1 && !adding}
            />
          ))}
          {adding && (
            <div className="flex items-center gap-2 px-4 py-3">
              <input
                autoFocus
                type="text"
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") handleAdd()
                  if (e.key === "Escape") {
                    setAdding(false)
                    setNewKey("")
                  }
                }}
                placeholder="field_key"
                className="flex-1 px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={handleAdd}
                disabled={!newKey.trim()}
                className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => {
                  setAdding(false)
                  setNewKey("")
                }}
                className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {!adding && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-primary-600 transition-colors"
        >
          <span className="text-base leading-none">+</span>
          Add field
        </button>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-600 mb-0.5">
          Exit CTA step
        </label>
        <p className="text-xs text-gray-400 mb-1.5">
          Step index at which the exit call-to-action appears (0 = disabled)
        </p>
        <input
          type="number"
          min={0}
          value={cfg.exit_cta_step ?? ""}
          onChange={e => {
            const val = e.target.value
            setConfigField("exit_cta_step", val === "" ? 0 : Number(val))
          }}
          placeholder="0"
          className="w-32 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>
    </div>
  )
}

export default LeadworkflowBehaviour
